import {
	AssetTypeCode,
	ComplexityLevel,
	DimensionUnit,
	MAX_ORIGINAL_CODE_LENGTH,
	MAX_ORIGINAL_CODES,
	MessageType,
	ReviewVerdict,
	WORKFLOW_STATUS_CODE_PATTERN,
	WorkOrderSource,
} from '@atlas/types'
import { z } from 'zod'

/** A classification tuple: axis code → selected value codes. */
const selectionSchema = z.record(z.string(), z.array(z.string()))

/** A link the page renders as `href`: http(s) only, so a `javascript:` URL can't become a clickable script. */
const webUrlSchema = z.url({ protocol: /^https?$/ })

/** A named related link (Veeva vault, Workfront, brief doc…). */
const workOrderLinkSchema = z.object({
	name: z.string().min(1),
	url: webUrlSchema,
})

const CURRENCIES = new Set(Intl.supportedValuesOf('currency'))

/**
 * A money value as a person enters it. The currency is optional because the entity defaults
 * it; an amount must already be whole minor units, since rounding a float would store a
 * value nobody typed.
 */
const moneyInputSchema = z.object({
	amountMinor: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
	currency: z
		.string()
		.refine((code) => CURRENCIES.has(code), { message: 'currency must be an ISO 4217 code, e.g. USD' })
		.optional(),
})

/** One original Veeva code as a person enters it; its position is its place in the list. */
const originalCodeInputSchema = z.object({
	code: z.string().trim().min(1).max(MAX_ORIGINAL_CODE_LENGTH),
	description: z.string().trim().max(500).nullable().optional(),
	link: webUrlSchema.nullable().optional(),
})

const originalCodesSchema = z.array(originalCodeInputSchema).max(MAX_ORIGINAL_CODES)

/** The external fields a person may set when creating a work order. */
const externalFieldsCreateShape = {
	budget: moneyInputSchema.optional(),
	plannedCost: moneyInputSchema.optional(),
	finalCost: moneyInputSchema.optional(),
	expectedPublishingDate: z.iso.date().optional(),
	originalCodes: originalCodesSchema.default([]),
}

/** The external fields a person may change on an existing work order; `null` clears one. */
const externalFieldsUpdateShape = {
	budget: moneyInputSchema.nullable(),
	plannedCost: moneyInputSchema.nullable(),
	finalCost: moneyInputSchema.nullable(),
	expectedPublishingDate: z.iso.date().nullable(),
	originalCodes: originalCodesSchema,
}

/**
 * Fields only the sync writes. Named so a request carrying one fails with the field in the
 * error, rather than being stripped and leaving the client believing it was saved.
 */
const SYNC_OWNED_FIELDS = [
	'externalRef',
	'externalObjectType',
	'externalReferenceNumber',
	'externalStatus',
	'externalClosedAt',
	'externalRemovedAt',
	'externalUpdatedAt',
	'externalVersion',
	'externalSyncedAt',
	'externalOwnerName',
	'externalOwnerEmail',
	'externalCostStatus',
	'plannedAssetCount',
	'isAdaptation',
	'isAdaptationSource',
	'adaptationNeeds',
	'scopeChanged',
	'scopeChangeReason',
	'referencedDocuments',
] as const

const syncOwnedShape = Object.fromEntries(
	SYNC_OWNED_FIELDS.map((field) => [
		field,
		z.never({ error: `${field} is set by the external system and cannot be written` }).optional(),
	]),
) as Record<(typeof SYNC_OWNED_FIELDS)[number], z.ZodOptional<z.ZodNever>>

/**
 * Create a work order (HTTP POST /work-orders body). `orderCode` and
 * `workOrderId` are generated server-side and are NOT accepted here. Complexity,
 * asset-type, both Veeva codes, duration and the campaign link are persisted as
 * classification attributes, not columns; `pmUserId` and `isContentFactory` are
 * workflow columns.
 */
export const workOrderCreateSchema = z
	.object({
		title: z.string().min(1),
		briefing: z.string().optional(),
		source: z.enum(WorkOrderSource).default(WorkOrderSource.MANUAL),
		dueAt: z.iso.datetime().optional(), // ISO datetime (date + time)
		parentWorkOrderId: z.uuid().optional(),
		pmUserId: z.uuid().optional(),
		isContentFactory: z.boolean().default(true),
		tuple: selectionSchema.default({}),
		complexityLevel: z.enum(ComplexityLevel).optional(),
		assetTypeCode: z.enum(AssetTypeCode).optional(),
		veevaCode: z.string().optional(), // the TARGET Veeva code
		// Always optional — relevant mainly to video deliverables, but never gated
		// on the asset type.
		durationSeconds: z.number().int().positive().optional(),
		campaignCode: z.string().optional(),
		links: z.array(workOrderLinkSchema).default([]),
		// assignees are `work_order_assignee.user_id` (a uuid column) — validate here so a
		// malformed id is a 400, not a Postgres type error deeper in the request.
		assigneeIds: z.array(z.uuid()).default([]),
		...externalFieldsCreateShape,
		...syncOwnedShape,
	})
	.superRefine((data, ctx) => {
		// The mandatory descriptors hold for every request through this schema.
		// They are NOT gated on `source`: that field is caller-supplied, so
		// exempting a non-MANUAL value would let any client opt out of the check
		// by declaring itself an integration. Workfront intake is unaffected
		// either way — it calls `createWorkOrder` directly and never validates
		// against this schema, so its best-effort `customFields` map still
		// produces an order for a payload that states neither descriptor.
		if (!data.complexityLevel)
			ctx.addIssue({ code: 'custom', path: ['complexityLevel'], message: 'Complexity level is required' })
		if (!data.assetTypeCode)
			ctx.addIssue({ code: 'custom', path: ['assetTypeCode'], message: 'Type of asset is required' })
	})

/** Longest free text a pack field takes: room for a brief, not a document. */
export const MAX_PACK_TEXT_LENGTH = 10_000

/** Pack text as the PM types it: blank text is stored as nothing. */
const packTextSchema = z
	.string()
	.max(MAX_PACK_TEXT_LENGTH)
	.nullable()
	.transform((value) => (value?.trim() ? value : null))

/** The main copy and its category; blank content clears it, like any blank pack text. */
const mainCopySchema = z
	.object({ content: z.string().max(MAX_PACK_TEXT_LENGTH), messageType: z.enum(MessageType) })
	.strict()
	.nullable()
	.transform((copy) => (copy?.content.trim() ? copy : null))

/**
 * What the PM writes down before the designer starts: absent leaves a field alone, `null` clears it.
 * Axis values (aspect ratio, platform) are not here: they are classification, checked against the registry.
 */
export const workOrderPackUpdateSchema = z
	.object({
		sharepointFolderUrl: webUrlSchema.nullable(),
		exactDimensions: packTextSchema,
		dimensionUnit: z.enum(DimensionUnit).nullable(),
		mainCopy: mainCopySchema,
		legalContentApplies: z.boolean().nullable(),
		mainCopyApplies: z.boolean().nullable(),
		legalTextApplies: z.boolean().nullable(),
		copyrightApplies: z.boolean().nullable(),
		aiLegalTextApplies: z.boolean().nullable(),
		isiApplies: z.boolean().nullable(),
		piApplies: z.boolean().nullable(),
	})
	.partial()
	.strict()

/**
 * Update a work order (HTTP PATCH /work-orders/{id} body). All fields optional;
 * `null` clears an optional value. The two mandatory descriptors may be changed
 * but never cleared — a manual order that has them must keep them.
 */
export const workOrderUpdateSchema = z
	.object({
		title: z.string().min(1),
		briefing: z.string().nullable(),
		dueAt: z.iso.datetime().nullable(),
		parentWorkOrderId: z.uuid().nullable(),
		pmUserId: z.uuid().nullable(),
		isContentFactory: z.boolean(),
		tuple: selectionSchema,
		complexityLevel: z.enum(ComplexityLevel).nullable(),
		assetTypeCode: z.enum(AssetTypeCode).nullable(),
		veevaCode: z.string().nullable(),
		durationSeconds: z.number().int().positive().nullable(),
		campaignCode: z.string().nullable(),
		links: z.array(workOrderLinkSchema),
		assigneeIds: z.array(z.uuid()),
		/** An `aspect_ratio` axis value code. */
		aspectRatio: z.string().min(1).nullable(),
		/** A `channel` axis value code: where the piece runs. */
		platform: z.string().min(1).nullable(),
		pack: workOrderPackUpdateSchema,
		...externalFieldsUpdateShape,
	})
	.partial()
	.extend(syncOwnedShape)
	.superRefine((data, ctx) => {
		// `null` is an explicit clear; `undefined` (absent) leaves the value alone.
		if (data.complexityLevel === null)
			ctx.addIssue({ code: 'custom', path: ['complexityLevel'], message: 'Complexity level cannot be cleared' })
		if (data.assetTypeCode === null)
			ctx.addIssue({ code: 'custom', path: ['assetTypeCode'], message: 'Type of asset cannot be cleared' })
	})

/**
 * `toStatus` is a syntactically valid workflow status code — non-empty,
 * lowercase ASCII letters/digits/underscores, compared and stored verbatim
 * (no normalization). Membership in the work order's assigned workflow, and
 * the legality of the edge to it, are verified by the transition engine and
 * never here - that keeps a syntactically valid but unreachable target
 * reaching the transition-domain audit boundary instead of being rejected
 * before the engine ever sees it.
 */
export const workOrderTransitionSchema = z.object({
	toStatus: z
		.string()
		.regex(WORKFLOW_STATUS_CODE_PATTERN, 'toStatus must be lowercase ASCII letters, digits, or underscores'),
})

/**
 * Request a validation asset (HTTP POST /work-orders/{id}/validation-asset-requests
 * body). `assetType` is the piece being produced (an `AssetTypeCode`); `channel` is
 * the target channel (e.g. `instagram`). The order + version are resolved server-side;
 * the delivery gate (final-visuals + messages present) is enforced in the service.
 */
export const validationAssetRequestSchema = z.object({
	assetType: z.enum(AssetTypeCode),
	channel: z.string().min(1),
})

/**
 * Mark or un-mark one deliverable on the order's current review stage (HTTP PUT
 * /work-orders/{id}/deliverables/{fileId}/review body). The stage is resolved
 * server-side from the order, never taken from the request.
 */
export const deliverableReviewSchema = z.object({
	verdict: z.enum(ReviewVerdict),
})

/**
 * Prepare an order's assets (HTTP POST /work-orders/{id}/prepare body). Optional.
 *
 * Every regeneration flag is off by default, because the route triggers PAID work and is
 * reachable by a shared credential — redoing finished work has to be asked for explicitly
 * rather than being what a stray second click gets.
 */
export const workOrderPrepareSchema = z.object({
	/**
	 * Redo BOTH copy and artwork. Kept for callers that do not distinguish them; the
	 * per-kind flags below are what a UI should send, since the two differ by an order of
	 * magnitude in cost and a user regenerating copy rarely means "and pay for three images
	 * too".
	 */
	force: z.boolean().optional(),
	/** Regenerate artwork that already exists. */
	forceImages: z.boolean().optional(),
	/** Re-translate copy that already exists. */
	forceTranslations: z.boolean().optional(),
	/**
	 * Report what would be queued and queue NOTHING.
	 *
	 * The route's whole risk is spending money, so being able to ask what it would do —
	 * without doing it — is the difference between a safe demo and a hopeful one. It is also
	 * how a UI shows "3 images, 6 translations" before the user commits.
	 */
	dryRun: z.boolean().optional(),
	/**
	 * Pixel SIZES to ensure artwork exists for, as `WxH`.
	 *
	 * Sizes, not "formats". A format is a TEMPLATE concept — `1080x1520` is one of a
	 * family's declared variants, and only the rendering surface knows which family it is
	 * producing against. This endpoint is a domain capability: "make sure this order has
	 * artwork at these dimensions". Naming the parameter `formats` put a template vocabulary
	 * in a domain service and made a generic capability look Figma-specific; the integration
	 * translates its family's formats into sizes before calling.
	 *
	 * Omitted means copy only: generating artwork for a guessed size would pay for an asset
	 * no deliverable uses.
	 */
	sizes: z.array(z.string().regex(/^\d+x\d+$/, 'sizes must be WxH, e.g. 1080x1520')).optional(),
})

/**
 * What an order's artwork should be generated from.
 *
 * `renderedText` is supplied rather than derived: the section has already rendered
 * it through the prompt service to show the author what will be sent, and the row
 * exists to record what was AGREED — re-rendering server-side could store something
 * different from what they approved.
 *
 * `promptVersionId` is required for the same reason. A prompt id alone would leave
 * the record pointing at a mutable head, so a later edit would change what this
 * order appears to have generated from.
 */
export const workOrderGenerationContextSchema = z.object({
	promptId: z.string().uuid(),
	promptVersionId: z.string().uuid(),
	/** Variable key → chosen value(s), as submitted. */
	values: z.record(z.string(), z.unknown()).default({}),
	renderedText: z.string().min(1),
	negativePrompt: z.string().optional(),
})

export type DeliverableReviewInput = z.infer<typeof deliverableReviewSchema>
export type WorkOrderCreateInput = z.infer<typeof workOrderCreateSchema>
export type WorkOrderGenerationContextInput = z.infer<typeof workOrderGenerationContextSchema>
export type WorkOrderPrepareInput = z.infer<typeof workOrderPrepareSchema>
export type WorkOrderUpdateInput = z.infer<typeof workOrderUpdateSchema>
export type WorkOrderPackUpdateInput = z.infer<typeof workOrderPackUpdateSchema>
export type WorkOrderTransitionInput = z.infer<typeof workOrderTransitionSchema>
export type ValidationAssetRequestInput = z.infer<typeof validationAssetRequestSchema>
export type MoneyInput = z.infer<typeof moneyInputSchema>
export type OriginalCodeInput = z.infer<typeof originalCodeInputSchema>
